import { traceEvent } from '@wk/wk-gatherer';
import { Button } from '@wk/wk-uni-lib';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useContext } from 'react';

import LeadButton from '@/components/LeadButton';
import { GlobalLoginContext } from '@/context/GlobalLoginContest';

/**
 * 媒体报道和奖项展示模块
 *
 * 响应式断点：
 * - h5: 单列布局，标题 28px
 * - lg: 3 列布局，标题 34px，卡片 312px
 * - xl: 3 列布局，标题 48px，卡片 440px
 */

/**
 * 奖杯图标（标题区域右下角装饰）
 */
const TrophyIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="40"
    height="46"
    viewBox="0 0 40 46"
    fill="none"
    className="absolute bottom-0 right-0 hidden h-[46px] w-10 lg:block">
    <g opacity="0.7">
      <path
        d="M23.6977 31.4757C31.0989 31.4757 37.0264 25.2525 37.0266 17.6676V4.06385C37.0266 3.23808 36.3632 2.56836 35.5453 2.56836H11.85C11.0321 2.56836 10.3688 3.23808 10.3688 4.06385V17.6676C10.369 25.2525 16.2965 31.4757 23.6977 31.4757Z"
        fill="#EDE5D3"
      />
      <path
        d="M11.4461 10.2422C12.1946 10.2422 12.8016 10.8551 12.8016 11.6107V20.733C12.8014 21.4885 12.1945 22.1008 11.4461 22.1008C6.95282 22.1008 2.86255 18.3086 2.86255 11.6107C2.86255 10.8551 3.46958 10.2422 4.21807 10.2422H11.4461Z"
        stroke="#EDE5D3"
        strokeWidth="3.61978"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M35.9477 10.2422C35.1992 10.2422 34.5922 10.8551 34.5922 11.6107V20.733C34.5924 21.4885 35.1993 22.1008 35.9477 22.1008C40.441 22.1008 44.5312 18.3086 44.5312 11.6107C44.5312 10.8551 43.9242 10.2422 43.1757 10.2422H35.9477Z"
        stroke="#EDE5D3"
        strokeWidth="3.61978"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M23.6969 31.9707V35.9579"
        stroke="#EDE5D3"
        strokeWidth="2.97819"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M28.8616 33.6661L28.6765 33.6543H18.4554C17.9732 33.6544 17.5231 33.8913 17.2468 34.2853L17.1944 34.3665L13.5507 40.3469C13.2697 40.8082 13.2579 41.3871 13.5194 41.8598C13.7812 42.3326 14.2759 42.6257 14.8124 42.6257H32.5843C33.129 42.6255 33.63 42.3235 33.8882 41.8393C34.1463 41.355 34.1201 40.7666 33.8202 40.3075L29.9132 34.3263C29.6734 33.9593 29.2876 33.7202 28.8616 33.6661Z"
        fill="#EDE5D3"
      />
    </g>
  </svg>
);

/**
 * 单个奖项卡片
 */
interface AwardCardProps {
  title: string;
  description: string;
  link?: string;
}

const AwardCard: React.FC<AwardCardProps> = ({ title, description, link }) => {
  const CardContent = (
    <div className="relative flex h-full w-full flex-col overflow-hidden rounded-md border-[1px] border-[#ece8dc] bg-bg-card_white bg-clip-padding">
      {/* 标题区域 */}
      <div className="relative flex w-full items-center justify-center bg-[#fcf9f2] p-4 lg:h-[104px] lg:p-6 xl:h-[112px] xl:px-8 xl:py-6">
        <h3 className="flex-1 overflow-hidden text-ellipsis text-[20px] font-bold leading-[1.4] text-text-primary lg:line-clamp-2 lg:text-[20px] xl:text-[24px]">
          {title}
        </h3>
        <TrophyIcon />
      </div>

      {/* 描述区域 */}
      <div className="flex w-full flex-1 items-start bg-bg-card_white px-4 py-6 lg:p-6 xl:px-8 xl:py-6">
        <p className="w-full whitespace-pre-wrap text-[16px] leading-[1.4] text-text-secondary_1 lg:text-[14px] xl:text-[16px]">
          {description}
        </p>
      </div>
    </div>
  );

  // 如果有链接，返回可点击的 a 标签
  if (link) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="block flex-1 transition-transform hover:scale-[1.02]">
        {CardContent}
      </a>
    );
  }

  // 没有链接则直接返回卡片（也需要 flex-1）
  return <div className="flex-1">{CardContent}</div>;
};

/**
 * 媒体报道模块
 */
const MediaSection: React.FC = () => {
  const { t, i18n } = useTranslation('wukong-reviews');
  const isZh = i18n.language === 'zh';
  const { isLogin } = useContext(GlobalLoginContext);

  // 约课按钮埋点
  const handleBookClassTrace = useCallback(() => {
    traceEvent({
      data: {
        _event: 'C_WallofLove_BookClass',
        dsSource: '用户之声-媒体背书',
        loginStatus: isLogin ? '已登' : '未登',
      },
    });
  }, [isLogin]);

  const awards = [
    {
      title: t('媒体-1-标题'),
      description: t('媒体-1-介绍'),
      link: 'https://news.qq.com/rain/a/20241010A07IOF00',
    },
    {
      title: t('媒体-2-标题'),
      description: t('媒体-2-介绍'),
      link: 'https://apnews.com/press-release/pr-newswire/school-curricula-0163ef4da1edc53eb6169508b6929b91',
    },
    {
      title: t('媒体-3-标题'),
      description: t('媒体-3-介绍'),
      link: 'https://www.prnewswire.com/news-releases/wukong-chinese-ranked-no1-globally-based-on-cumulative-paying-users-according-to-frost--sullivan-302687368.html',
    },
  ];

  return (
    <section className="flex w-full flex-col items-center space-y-6 bg-white px-4 pb-[100px] pt-0 lg:space-y-12 lg:px-8 lg:py-0 xl:space-y-[60px]">
      {/* 标题区域 */}
      <div className="flex w-full items-center justify-center space-x-2">
        <h2
          className={`text-center text-[28px] font-black leading-normal text-text-primary lg:text-[34px] xl:text-[48px] [&>span]:text-brand-red-100 ${isZh ? 'font-sourceHanSans' : ''}`}
          dangerouslySetInnerHTML={{ __html: t('媒体-标题') }}
        />
      </div>

      {/* 媒体 Logo - PC 端和移动端分别使用不同图片 */}
      <div className="flex w-full items-center justify-center">
        {/* PC 端 Logo */}
        <div className="relative hidden h-[78px] w-[984px] lg:block xl:w-[1320px]">
          <Image
            src="/images/wukong-reviews/logo.png"
            alt="Media Logos"
            fill
            unoptimized
            className="object-contain"
          />
        </div>
        {/* 移动端 Logo */}
        <div className="relative h-[128px] w-full lg:hidden">
          <Image
            src="/images/wukong-reviews/logo-phone.png"
            alt="Media Logos"
            fill
            unoptimized
            className="object-contain"
          />
        </div>
      </div>

      {/* 奖项卡片 */}
      <div className="flex w-full max-w-[984px] flex-col space-y-4 lg:flex-row lg:space-x-4 lg:space-y-0 xl:max-w-[1320px] xl:space-x-6">
        {awards.map((award, index) => (
          <AwardCard
            key={index}
            title={award.title}
            description={award.description}
            link={award.link}
          />
        ))}
      </div>

      {/* CTA 按钮 */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={handleBookClassTrace}>
        <LeadButton
          asCustomButton
          callbackUrl={
            typeof window !== 'undefined' ? window.location.href : ''
          }
          traceName="O_WallOfLove_MediaSection_CTAClick">
          <Button
            size="large"
            className="h-[52px] px-[30px] text-[20px] font-bold lg:h-[55px] lg:px-12 lg:text-[18px] xl:h-[77px] xl:px-[64px] xl:text-[24px]">
            {t('媒体-按钮')}
          </Button>
        </LeadButton>
      </div>
    </section>
  );
};

export default MediaSection;
